/* @flow */

import React from 'react';
import glamorous from 'glamorous';
import { padding } from 'polished';
import numeral from 'numeral';
import { get } from 'lodash/fp';

import { hover } from 'app/utils/cssUtils';
import AnimateNumber from 'app/components/commons/AnimateNumber';
import LivePlayerWidget from 'app/widgets/one-sport/LivePlayerWidget';
import PlayerAvatar from 'app/components/challenge/PlayerAvatar';
import PlayerBar from 'app/components/challenge/PlayerBar';
import PlayerDatum from 'app/components/challenge/PlayerDatum';
import Tappable from 'app/components/commons/Tappable';
import { IN_PROGRESS } from 'app/constants/challengeStatusTypes';
import StatusIcon from 'app/components/challenge/StatusIcon';
import OneScoreWidget from 'app/widgets/one-sport/OneScoreWidget';

import StarSymbol from './StarSymbol';
import ValueGraph from './ValueGraph';
import LineGraph from './LineGraph';
import { isInProgress } from '../../utils/challengeUtils';

type Props = {
  awayTeam?: string,
  compressedWidget?: boolean,
  gameOrder?: number,
  gamePlaced?: boolean,
  gameStage?: string,
  homeTeam?: string,
  id: string,
  idStr: string,
  image?: string,
  initials: string,
  isHome: boolean,
  isTeam: boolean,
  isRacing?: boolean,
  isScratched?: boolean,
  labels: Array<Object>,
  name: string,
  number?: number,
  onWidgetClick?: string => void,
  playerBoardTableInfo: Object,
  position: string,
  positionFilter: string,
  progress?: string,
  rank: number,
  reverseOrder?: boolean,
  rider?: string,
  riderImage?: string,
  selected: boolean,
  showWidget: boolean,
  statusId: number,
  tableData?: Object,
  trainer?: string,
  win?: number,
  place?: number
};

const EntityWrapper = glamorous.div(({ selected, theme }) => ({
  backgroundColor: selected ? '#f4f4f5' : theme.color.white,
  borderColor: theme.color.basis6,
  borderStyle: 'solid',
  borderTopWidth: 0,
  borderRightWidth: 0,
  borderBottomWidth: 1,
  borderLeftWidth: 0,
  color: theme.color.basis1,
  display: 'flex',
  fontSize: '0.9rem',
  position: 'relative',
  transition: 'background-color 0.2s',
  ...hover({
    backgroundColor: theme.color.basis6
  })
}));

const EntityMain = glamorous.div({
  display: 'flex',
  flexGrow: 1,
  minWidth: 0 // flexbox hack
});

const LiveStats = glamorous.div(({ theme }) => ({
  alignItems: 'flex-end',
  display: 'flex',
  flexBasis: 'auto',
  flexDirection: 'column',
  flexGrow: 0,
  flexShrink: 0,
  justifyContent: 'space-between',
  textAlign: 'center',
  padding: 4,
  [theme.mediaQuery.tabletPortraitAndUp]: {
    alignItems: 'center',
    flexBasis: '16rem', // auto doesn't seem to work
    flexDirection: 'row-reverse',
    fontSize: '0.833rem',
    justifyContent: 'flex-end'
  }
}));

const LiveStatsInfo = glamorous.div(({ theme }) => ({
  display: 'flex',
  width: '10.5em',
  justifyContent: 'flex-end',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  [theme.mediaQuery.tabletPortraitAndUp]: {
    width: 'auto',
    justifyContent: 'flex-start',
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1
  }
}));

const LiveRank = glamorous.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  fontSize: theme.fontSize.xxsmall,
  flexBasis: '2rem',
  flexGrow: 0,
  flexShrink: 0,
  fontWeight: 600,
  justifyContent: 'center',
  [theme.mediaQuery.desktopAndUp]: {
    flexBasis: '2.5rem',
    fontSize: theme.fontSize.default
  }
}));

const NumberWrapper = glamorous.div(({ theme, selected }) => ({
  color: selected ? theme.color.white : theme.color.basis1,
  backgroundColor: selected ? theme.color.primary : 'transparent',
  ...padding(2, 4),
  marginRight: -4,
  borderRadius: 8,
  fontSize: theme.fontSize.default,
  fontWeight: theme.fontWeight.demiBold,
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    fontSize: 'inherit',
    ...padding(6, 8)
  }
}));

const AvatarCell = glamorous.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  ...padding(null, 5),
  position: 'relative',
  [theme.mediaQuery.mobileOnly]: {
    display: 'none'
  }
}));

const StatusIconWrapper = glamorous.div(({ live, theme }) => ({
  backgroundImage: `linear-gradient(135deg, ${
    live ? theme.color.primary : theme.color.basis1
  } 50%, transparent 50%)`,
  color: 'white',
  fontSize: '0.5rem',
  height: '2.2em',
  left: 0,
  position: 'absolute',
  padding: 1,
  transform: 'rotate(360deg)', // stop jitter effect
  top: 0,
  width: '2.2em'
}));

const PlayerBarOuter = glamorous.div({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  minWidth: 0, // flexbox hack
  position: 'relative',
  display: 'flex',
  alignItems: 'center'
});

class LiveEntityRow extends React.PureComponent<Props> {
  props: Props;

  state = {
    animateNumbers: true
  };

  static defaultProps = {
    awayTeam: '',
    compressedWidget: false,
    gameOrder: undefined,
    gamePlaced: false,
    gameStage: undefined,
    homeTeam: '',
    image: undefined,
    isRacing: false,
    isScratched: false,
    number: undefined,
    onWidgetClick: undefined,
    progress: undefined,
    reverseOrder: false,
    rider: undefined,
    riderImage: undefined,
    tableData: undefined,
    trainer: undefined,
    win: undefined,
    place: undefined
  };

  componentWillUpdate = nextProps => {
    const { positionFilter } = this.props;

    // we don't want to animate numbers when we are changing positionFilter
    this.state.animateNumbers = positionFilter === nextProps.positionFilter;
  };

  handleWidgetClick = () => {
    const { idStr, onWidgetClick } = this.props;

    if (onWidgetClick) {
      onWidgetClick(idStr);
    }
  };

  render() {
    const {
      awayTeam,
      compressedWidget,
      gameOrder,
      gamePlaced,
      gameStage,
      homeTeam,
      id,
      image,
      initials,
      isHome,
      isTeam,
      isRacing,
      isScratched,
      labels,
      name,
      number,
      playerBoardTableInfo,
      position,
      progress,
      rank,
      reverseOrder,
      rider,
      riderImage,
      selected,
      showWidget,
      statusId,
      tableData,
      trainer,
      win,
      place
    } = this.props;

    const { animateNumbers } = this.state;

    const avatarImage = isRacing ? riderImage : image;

    return (
      <div>
        <Tappable component={EntityWrapper} onTap={this.handleWidgetClick} selected={selected}>
          <StatusIconWrapper live={isInProgress(statusId)}>
            <StatusIcon statusId={statusId} />
          </StatusIconWrapper>
          <LiveRank>
            <div>#{rank}</div>
          </LiveRank>
          {(avatarImage || initials) && (
            <AvatarCell>
              <PlayerAvatar fallbackText={initials} image={avatarImage} />
            </AvatarCell>
          )}
          <EntityMain>
            <PlayerBarOuter>
              <PlayerBar
                awayTeam={awayTeam}
                gameOrder={gameOrder}
                homeTeam={homeTeam}
                isHome={isHome}
                isRacing={isRacing}
                isScratched={isScratched}
                labels={labels}
                name={name}
                number={number}
                position={position}
                progress={isInProgress(statusId) ? progress : undefined}
                reverseOrder={reverseOrder}
                rider={rider}
                selected={selected}
                stage={gameStage}
                trainer={trainer}
              />
            </PlayerBarOuter>
            <LiveStats>
              <PlayerDatum desktopSize="small">
                <NumberWrapper selected={selected}>
                  <AnimateNumber
                    animate={animateNumbers}
                    format={get(playerBoardTableInfo.defaultColumn.format, tableData) || '0.00'}
                    number={get(playerBoardTableInfo.defaultColumn.field, tableData)}
                    selected={selected}
                  />
                </NumberWrapper>
              </PlayerDatum>
              <LiveStatsInfo>
                {playerBoardTableInfo &&
                  playerBoardTableInfo.columns &&
                  playerBoardTableInfo.columns
                    .filter(
                      ({ field, showInPlayerBar = false }) =>
                        field !== playerBoardTableInfo.defaultColumn.field && !showInPlayerBar
                    )
                    .map(
                      ({
                        field,
                        format = '0.0',
                        label,
                        size = 'small',
                        mobileSize = 'small',
                        showStarSymbolForScheduled = false
                      }) => {
                        let data = '\u2012';

                        if (get(field, tableData)) {
                          switch (format) {
                            case 'lineGraph':
                              data = <LineGraph value={get(field, tableData)} />;
                              break;
                            case 'valueGraph':
                              data = <ValueGraph value={get(field, tableData)} />;
                              break;
                            default:
                              data = numeral(get(field, tableData)).format(format);
                          }
                        }
                        return (
                          <PlayerDatum
                            key={field}
                            label={label}
                            mobileSize={mobileSize}
                            desktopSize={size}
                          >
                            {data}
                            {showStarSymbolForScheduled &&
                              statusId <= IN_PROGRESS &&
                              get(field, tableData) > 0 && <StarSymbol />}
                          </PlayerDatum>
                        );
                      }
                    )}
              </LiveStatsInfo>
            </LiveStats>
          </EntityMain>
        </Tappable>
        {statusId < IN_PROGRESS || (isRacing && !gamePlaced) ? (
          <OneScoreWidget
            id={id}
            isOpen={showWidget}
            isRacing={isRacing}
            isTeam={isTeam}
            onClose={this.handleWidgetClick}
          />
        ) : (
          <LivePlayerWidget
            enablePolling={isInProgress(statusId)}
            id={id}
            isCompressed={compressedWidget}
            gamePlaced={gamePlaced}
            isRacing={isRacing}
            isOpen={showWidget}
            isTeam={isTeam}
            onClose={this.handleWidgetClick}
            win={win}
            place={place}
          />
        )}
      </div>
    );
  }
}

export default LiveEntityRow;
